import { useQuery } from "@tanstack/react-query";
import { Droplets, AlertTriangle, Check, Minus } from "lucide-react";
import { fetchSnapshot, fetchRiskTone } from "@/lib/api";
import { oilRiskCrossRead } from "@/lib/crossReads";

const posCls = (d) => (d === "up" ? "text-pos" : d === "down" ? "text-neg" : "text-zinc-400");
const arrow = (d) => (d === "up" ? "▲" : d === "down" ? "▼" : "•");

const STATUS = {
  conflict: { label: "Conflict", cls: "text-neg border-red-500/50", Icon: AlertTriangle },
  aligned: { label: "Aligned", cls: "text-pos border-emerald-500/50", Icon: Check },
  neutral: { label: "Neutral", cls: "text-zinc-400 border-zinc-700", Icon: Minus },
};

export default function OilRiskRead() {
  const snapQ = useQuery({ queryKey: ["snapshot"], queryFn: fetchSnapshot, staleTime: 1000 * 60 * 10 });
  const riskQ = useQuery({ queryKey: ["risk-tone"], queryFn: fetchRiskTone, staleTime: 1000 * 60 * 10 });

  const wti = (snapQ.data?.markets || []).find((m) => m.symbol === "WTI");
  const { status, oil: o, risk: r, note } = oilRiskCrossRead(wti, riskQ.data);
  const vix = (riskQ.data?.factors || []).find((f) => f.key === "vix")?.value;
  const st = STATUS[status];
  const loading = snapQ.isLoading || riskQ.isLoading;

  return (
    <div className="border border-zinc-800 bg-[#09090B]" data-testid="oil-risk-read">
      <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5">
          <Droplets size={11} strokeWidth={1.5} className="text-amber-term" /> Oil ⇄ Risk Cross-Read
        </span>
        {!loading && (
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-widest border ${st.cls} ${status === "conflict" ? "animate-pulse" : ""}`}
            data-testid="oil-risk-status"
          >
            <st.Icon size={10} strokeWidth={2} /> {st.label}
          </span>
        )}
      </div>

      {loading ? (
        <div className="h-[120px] flex items-center justify-center text-zinc-600 text-[11px] uppercase tracking-widest">
          Reading crude vs risk…
        </div>
      ) : (
        <div className="px-3 py-2.5 space-y-2">
          <div className="flex items-center justify-between" data-testid="oil-risk-wti">
            <span className="text-[11px] uppercase tracking-widest text-zinc-500">WTI positioning</span>
            <span className={`text-[11px] uppercase tracking-widest tabular-nums font-semibold ${posCls(o.dir)}`}>
              {arrow(o.dir)} {o.label}
              {o.idx != null && <span className="text-zinc-600 font-normal"> · COT idx {o.idx}</span>}
            </span>
          </div>
          <div className="flex items-center justify-between" data-testid="oil-risk-tone">
            <span className="text-[11px] uppercase tracking-widest text-zinc-500">Macro risk tone</span>
            <span className={`text-[11px] uppercase tracking-widest tabular-nums font-semibold ${posCls(r.dir)}`}>
              {arrow(r.dir)} {r.label}
              {vix != null && <span className="text-zinc-600 font-normal"> · VIX {vix}</span>}
            </span>
          </div>
          <p className="text-[11px] text-zinc-300 leading-relaxed pt-1 border-t border-zinc-900">{note}</p>
        </div>
      )}
    </div>
  );
}
